import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { ArrowRight, ArrowLeft, Building2, GraduationCap, Users, Globe, CheckCircle, Zap, Send, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NewMindsContactForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");
  const inputClass = "w-full bg-white border border-[#ebe5dc] rounded-xl px-4 py-3 text-sm text-[#2d2926] placeholder-[#b8afa4] focus:border-[#c4683c] focus:ring-1 focus:ring-[#c4683c] transition-all outline-none";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, { ...form, message: `[New Minds Inquiry] ${form.message}` });
      setStatus("success");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch { setStatus("error"); }
  };

  if (status === "success") {
    return (
      <div className="warm-card p-8 flex flex-col items-center text-center" data-testid="nm-contact-success">
        <CheckCircle className="text-[#c4683c] w-12 h-12 mb-4" />
        <h3 className="text-xl font-semibold text-[#2d2926] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>Thank you!</h3>
        <p className="text-sm text-[#5c564e]">We'll get back to you within 24 hours with a tailored proposal.</p>
        <button onClick={() => setStatus("idle")} className="mt-6 text-sm text-[#c4683c] hover:text-[#a5553a]">Submit another inquiry</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="warm-card p-6 space-y-4" data-testid="nm-contact-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input type="text" required placeholder="Your Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inputClass} data-testid="nm-input-name" />
        <input type="email" required placeholder="Work Email *" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={inputClass} data-testid="nm-input-email" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input type="text" placeholder="Organization Name" value={form.company} onChange={e => setForm({...form, company: e.target.value})} className={inputClass} data-testid="nm-input-company" />
        <input type="tel" placeholder="Phone Number" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className={inputClass} data-testid="nm-input-phone" />
      </div>
      <textarea required placeholder="Tell us about your team/organization and what you're looking for... *" rows={4} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className={`${inputClass} resize-none`} data-testid="nm-input-message" />
      {status === "error" && <p className="text-sm text-red-500">Something went wrong. Please try again.</p>}
      <button type="submit" disabled={status === "loading"} data-testid="nm-submit-button"
        className="w-full bg-[#c4683c] text-white px-8 py-3.5 rounded-full font-bold hover:bg-[#a5553a] transition-all flex items-center justify-center gap-2 disabled:opacity-50">
        {status === "loading" ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Send size={16} /> Book a Discovery Call</>}
      </button>
    </form>
  );
}

const audiences = [
  { icon: Building2, title: "For Corporates & Enterprises", subtitle: "L&D / HR / CXO", items: ["Team-wide AI adoption workshops (1-5 days)", "Custom AI tool training for specific departments", "Executive AI literacy & strategy sessions", "Hands-on agentic workflow training for ops teams", "Post-training support & implementation guidance"], cta: "Bring AI Coaching to Your Team" },
  { icon: GraduationCap, title: "For Universities & Institutions", subtitle: "Career Services / Faculty / Dept Heads", items: ["AI skill-building programs for student career readiness", "Faculty upskilling workshops on AI in teaching & research", "Industry-linked AI project mentorship for final-year students", "AI curriculum advisory for department modernization", "Campus-wide AI awareness programs"], cta: "Bring AI Coaching to Your Campus" },
  { icon: Users, title: "For Growing Businesses", subtitle: "Founders / Ops Leads / Team Leads", items: ["Practical AI for non-technical teams (sales, marketing, ops)", "Workflow automation coaching — do more with less", "Data literacy & AI decision-making for team leads", "Custom cohort programs for org-wide AI adoption", "Monthly AI office hours for ongoing guidance"], cta: "Bring AI Coaching to Your Organization" },
];

export default function NewMinds() {
  useEffect(() => {
    const originalTitle = document.title;
    document.title = "New Minds by BharatIntelliAI | AI Coaching for Organizations";
    const meta = document.querySelector('meta[name="description"]');
    const originalDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "Practical AI coaching and skill-building programs for corporates, universities, and growing businesses. Delivered remotely worldwide by BharatIntelliAI.");
    return () => {
      document.title = originalTitle;
      if (meta && originalDesc) meta.setAttribute("content", originalDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#faf7f2]/90 backdrop-blur-xl border-b border-[#ebe5dc]">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <span className="font-bold tracking-tight text-xl text-[#2d2926]" style={{ fontFamily: 'Fraunces, serif' }}>
              New <span className="text-[#c4683c]">Minds</span>
            </span>
            <span className="text-[10px] text-[#2d2926] font-semibold border border-[#d5cec5] px-2 py-0.5 rounded-full bg-white">by BharatIntelliAI</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-[#5c564e] hover:text-[#2a9d8f] transition-colors flex items-center gap-1"><ArrowLeft size={14} /> BharatIntelliAI</Link>
            <a href="#nm-contact" className="bg-[#c4683c] text-white px-5 py-2 rounded-full text-sm font-bold hover:bg-[#a5553a] transition-all">Book a Discovery Call</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 md:py-28" data-testid="nm-hero">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs font-bold uppercase tracking-[0.2em] text-[#c4683c] mb-6">AI & Digital Coaching for Organizations</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2d2926] leading-[1.1]" style={{ fontFamily: 'Fraunces, serif' }}>
            Get your team using AI <span className="text-[#c4683c]">this month</span>, not next year.
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="text-base md:text-lg text-[#5c564e] mt-6 max-w-2xl mx-auto leading-relaxed">
            Practical, results-first AI coaching and skill-building programs for corporates, universities, and growing businesses. Delivered remotely worldwide. No theory-heavy workshops — your people start building with AI from day one.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
            <a href="#nm-contact" className="inline-flex items-center justify-center gap-2 bg-[#c4683c] text-white px-8 py-3.5 rounded-full font-bold hover:bg-[#a5553a] transition-all group">
              Book a Discovery Call for Your Organization <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Why New Minds */}
      <section className="py-16 warm-section-alt">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: "Action, Not Theory", desc: "Your teams leave every session with something they can use tomorrow — not a certificate they'll forget." },
              { icon: Globe, title: "Delivered Worldwide", desc: "AI coaching and skill-building for organizations, delivered remotely across India and internationally." },
              { icon: CheckCircle, title: "Shaped by Practice", desc: "Every program is built on 20+ years of real enterprise experience — we teach what we've actually implemented." },
            ].map((c, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="warm-card p-6 text-center">
                <c.icon className="w-8 h-8 text-[#c4683c] mx-auto mb-3" strokeWidth={1.5} />
                <h3 className="text-base font-semibold text-[#2d2926] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>{c.title}</h3>
                <p className="text-sm text-[#5c564e]">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience Cards */}
      <section className="py-20" data-testid="nm-audiences">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#c4683c] mb-3">Who We Serve</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#2d2926]" style={{ fontFamily: 'Fraunces, serif' }}>AI coaching tailored for your organization</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {audiences.map((a, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="warm-card p-7 flex flex-col" data-testid={`nm-audience-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 bg-[#c4683c]/10 border border-[#c4683c]/20 rounded-xl flex items-center justify-center">
                    <a.icon className="w-5 h-5 text-[#c4683c]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#2d2926]" style={{ fontFamily: 'Fraunces, serif' }}>{a.title}</h3>
                    <p className="text-[10px] text-[#8a8078]">{a.subtitle}</p>
                  </div>
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {a.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs text-[#5c564e]">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#c4683c] mt-1.5 flex-shrink-0" />{item}
                    </li>
                  ))}
                </ul>
                <a href="#nm-contact" className="inline-flex items-center gap-2 text-[#c4683c] font-medium text-sm hover:text-[#a5553a] transition-colors">
                  {a.cta} <ArrowRight size={14} />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="nm-contact" className="py-20 warm-section-alt" data-testid="nm-contact">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#2d2926] mb-4" style={{ fontFamily: 'Fraunces, serif' }}>Ready to bring AI coaching to your organization?</h2>
              <p className="text-sm text-[#5c564e] mb-6 leading-relaxed">Whether you're a corporate L&D head, university career services director, or a business founder — fill in your details and we'll get back within 24 hours with a tailored proposal.</p>
              <div className="space-y-3">
                {["Free discovery call — no commitment", "Custom program designed for your team", "Delivered remotely worldwide", "Engagements in INR and USD"].map((t, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#c4683c] mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-[#5c564e]">{t}</p>
                  </div>
                ))}
              </div>
            </div>
            <NewMindsContactForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-[#ebe5dc]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs text-[#8a8078]">New Minds — AI & Digital Coaching by <a href="/" className="text-[#2a9d8f] hover:text-[#238b7f]">BharatIntelliAI</a> (Rewat Intelliforge Technologies LLP)</p>
          <p className="text-xs text-[#b8afa4] mt-1">Serving organizations in India and internationally</p>
        </div>
      </footer>
    </div>
  );
}
